import * as React from 'react'
import { Badge, type BadgeProps } from './Badge'

/**
 * ApplicationState — the IntelliAuth Application operational-state closed
 * set, mirroring the v2 framework's `dp/internal/core/application.AppState`
 * enum (mig 0035) and the wire `components.schemas.Application.state` field
 * emitted by the dp-auth-api. Distinct from soft-delete: a deleted app is
 * inactive regardless of state, but the UI typically renders the deleted
 * state via a separate badge so this enum stays a two-state operational
 * toggle.
 */
export type ApplicationState = 'active' | 'disabled'

const APPLICATION_STATE_META: Record<
  ApplicationState,
  { variant: BadgeProps['variant']; label: string }
> = {
  active: { variant: 'success', label: 'Enabled' },
  disabled: { variant: 'error', label: 'Disabled' },
}

export interface ApplicationStateBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  /** The v2 ApplicationState; renders as a coloured Badge with the canonical label. */
  state: ApplicationState
  /** Optional override for the rendered label (defaults to 'Enabled' / 'Disabled'). */
  label?: string
}

/**
 * ApplicationStateBadge — semantic wrapper over Badge that internalises
 * the AppState -> Badge variant + label mapping. Use everywhere the
 * admin UI surfaces an Application's operational state so the cell tone
 * + copy stay consistent across the list + detail surfaces.
 */
export const ApplicationStateBadge = React.forwardRef<HTMLSpanElement, ApplicationStateBadgeProps>(
  ({ state, label, size, ...rest }, ref) => {
    const meta = APPLICATION_STATE_META[state]
    return (
      <Badge ref={ref} variant={meta.variant} size={size} {...rest}>
        {label ?? meta.label}
      </Badge>
    )
  },
)
ApplicationStateBadge.displayName = 'ApplicationStateBadge'
